import { openDB } from 'idb'

const indexDBName = 'ewa'
const indexDBVersion = 10

class DataBase {
  constructor() {
    this.stores = [
      'words',
      'heatmap',
      'replicas',
      'resources',
      'lessons',
      'sync_events'
    ]
  }

  async getDb() {
    if (this.db) {
      return this.db
    }

    for (let attempt = 1; attempt <= 3; attempt += 1) {
      try {
        this.db = await openDB(indexDBName, indexDBVersion, {
          upgrade: (db) => {
            this.stores.forEach((store) => {
              if (!db.objectStoreNames.contains(store)) {
                db.createObjectStore(store, { autoIncrement: true })
              }
            })
          }
        })
      } catch (error) {
        if (error?.name === 'VersionError') {
          await this.recreateDatabase('incompatible version')
          continue
        }
        throw error
      }

      const missing = this.getMissingStores()
      if (missing.length) {
        await this.recreateDatabase(`missing stores: ${missing.join(', ')}`)
        continue
      }

      return this.db
    }

    throw new Error('Failed to open database after 3 attempts')
  }

  getMissingStores() {
    if (!this.db) {
      return []
    }

    return this.stores.filter(
      (store) => !this.db.objectStoreNames.contains(store)
    )
  }

  async recreateDatabase(reason) {
    console.warn('[db] Recreating database:', reason)

    if (this.db) {
      this.db.close()
      this.db = null
    }

    await this.deleteDatabase()
  }

  async deleteDatabase() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.deleteDatabase(indexDBName)

      request.onsuccess = () => resolve()
      request.onerror = () => reject(request.error)
      request.onblocked = () =>
        console.warn('[db] deleteDatabase blocked, waiting for other tabs')
    })
  }

  async set(storeName, key, data) {
    const db = await this.getDb()

    const tx = db.transaction(storeName, 'readwrite')
    const store = tx.objectStore(storeName)

    await store.put(data, key)
    await tx.done
    return data
  }

  async get(storeName, key) {
    const db = await this.getDb()

    const tx = db.transaction(storeName, 'readonly')
    const store = tx.objectStore(storeName)

    const val = await store.get(key)
    await tx.done
    return val
  }

  async drop(storeName, key) {
    const db = await this.getDb()

    const tx = db.transaction(storeName, 'readwrite')
    const store = tx.objectStore(storeName)

    await store.delete(key)
    await tx.done
  }

  async cleanup(key) {
    const db = await this.getDb()

    const operations = []
    const { stores } = this

    for (const storeName of stores) {
      const transaction = db.transaction(storeName, 'readwrite')
      const store = transaction.objectStore(storeName)

      operations.push(store.delete(key))
    }

    await Promise.all(operations)
  }
}

export default new DataBase()
